import { Bar, CartesianGrid, ComposedChart, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { BarChart3 } from "lucide-react";

import { CORES, compacto, reais, rotuloGrupo } from "../lib/formato";

const BALAO = { backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 };

/** Pontos do gráfico. Saídas vão para baixo do zero; o saldo vira duas séries
 *  (realizado cheio, previsto tracejado) que se encontram no último grupo passado. */
function pontos(serie, agrupar) {
  const ultimoPassado = serie.reduce((idx, l, i) => (!l.futuro ? i : idx), -1);
  return serie.map((l, i) => ({
    ...l,
    rotulo: rotuloGrupo(l.data, agrupar),
    saidasR: -l.saidas_realizadas,
    saidasP: -l.saidas_previstas,
    saldoR: i <= ultimoPassado ? l.saldo : null,
    saldoP: i >= ultimoPassado ? l.saldo : null,
  }));
}

function Balao({ active, payload }) {
  if (!active || !payload?.length) return null;
  const l = payload[0].payload;
  const linha = (rotulo, v, cor) => (
    <p className="flex justify-between gap-6"><span className="text-muted-foreground">{rotulo}</span><span className={`tabular-nums font-semibold ${cor || ""}`}>{reais(v)}</span></p>
  );
  return (
    <div className="rounded-lg border bg-popover px-3 py-2 text-xs shadow-lg min-w-[220px] space-y-0.5">
      <p className="font-semibold text-sm mb-1">{l.rotulo}{l.futuro ? " · previsto" : ""}</p>
      {linha("Entrou", l.entradas_realizadas, "text-emerald-600")}
      {l.entradas_previstas > 0 && linha("Vai entrar", l.entradas_previstas, "text-emerald-600/70")}
      {linha("Saiu", l.saidas_realizadas, "text-red-600")}
      {l.saidas_previstas > 0 && linha("Vai sair", l.saidas_previstas, "text-red-600/70")}
      <div className="border-t pt-1 mt-1">{linha("Saldo no fim", l.saldo, l.saldo < 0 ? "text-red-600" : "")}</div>
    </div>
  );
}

function Legenda() {
  const item = (cor, texto, tracejado) => (
    <span className="flex items-center gap-1.5">
      {tracejado ? <span className="w-5 border-t-2 border-dashed" style={{ borderColor: cor }} /> : <span className="w-3 h-3 rounded-sm" style={{ background: cor }} />}
      {texto}
    </span>
  );
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
      {item(CORES.entrada, "Entradas")}{item(CORES.saida, "Saídas")}{item(CORES.saldo, "Saldo")}
      {item(CORES.saldo, "Saldo previsto", true)}
      <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm opacity-40" style={{ background: CORES.entrada }} />mais claro = previsto</span>
    </div>
  );
}

export default function GraficoFluxo({ dados }) {
  const serie = dados?.serie || [];
  if (!serie.length) return null;
  const agrupar = dados.periodo.agrupar;
  const data = pontos(serie, agrupar);
  const { inicio, fim, hoje } = dados.periodo;
  const marcaHoje = inicio <= hoje && hoje <= fim ? [...data].reverse().find((l) => !l.futuro) : null;
  return (
    <section className="rounded-2xl border bg-card p-4 sm:p-5" data-e2e="grafico-fluxo">
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <BarChart3 className="w-5 h-5 text-primary" />
        <h2 className="text-lg font-bold">Entradas, saídas e saldo</h2>
        <div className="ml-auto"><Legenda /></div>
      </div>
      <ResponsiveContainer width="100%" height={340}>
        <ComposedChart data={data} stackOffset="sign" margin={{ top: 10, right: 12, left: 4, bottom: 0 }} barCategoryGap="18%">
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted))" vertical={false} />
          <XAxis dataKey="rotulo" stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 11 }} interval="preserveStartEnd" minTickGap={12} />
          <YAxis stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 11 }} tickFormatter={compacto} width={48} />
          <Tooltip content={<Balao />} contentStyle={BALAO} cursor={{ fill: "hsl(var(--muted))", opacity: 0.4 }} />
          <ReferenceLine y={0} stroke="hsl(var(--muted-foreground))" strokeOpacity={0.6} />
          {marcaHoje && <ReferenceLine x={marcaHoje.rotulo} stroke="hsl(var(--primary))" strokeDasharray="4 4"
            label={{ value: "hoje", position: "insideTopRight", fill: "hsl(var(--primary))", fontSize: 11, fontWeight: 600 }} />}
          <Bar dataKey="entradas_realizadas" stackId="v" fill={CORES.entrada} radius={[4, 4, 0, 0]} maxBarSize={28} />
          <Bar dataKey="entradas_previstas" stackId="v" fill={CORES.entrada} fillOpacity={0.4} radius={[4, 4, 0, 0]} maxBarSize={28} />
          <Bar dataKey="saidasR" stackId="v" fill={CORES.saida} radius={[4, 4, 0, 0]} maxBarSize={28} />
          <Bar dataKey="saidasP" stackId="v" fill={CORES.saida} fillOpacity={0.4} radius={[4, 4, 0, 0]} maxBarSize={28} />
          <Line type="monotone" dataKey="saldoR" stroke={CORES.saldo} strokeWidth={2} dot={false} activeDot={{ r: 5, strokeWidth: 2, stroke: "hsl(var(--card))" }} connectNulls={false} />
          <Line type="monotone" dataKey="saldoP" stroke={CORES.saldo} strokeWidth={2} strokeDasharray="6 5" dot={false} activeDot={{ r: 5 }} connectNulls={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </section>
  );
}
